import { v4 } from "uuid";
import { api } from "../api";
import { config } from "../data/config";
import convertTemplateInAnswer from "./convertTemplateInAnswer";

export async function saveScenarioFlow(
  nodes,
  edges,
  scenarioId,
  campaignId,
  user,
  campaignName,
) {
  const forceJsonFormat = (str) => {
    try {
      return JSON.parse(str);
    } catch (e) {
      console.error(e);
    }
  };

  async function processNodeCards(
    node,
    user,
    config,
    scenarioId,
    api,
    officialWhatsappTemplate,
    whatsappTemplateId,
    flowNodeRecord,
  ) {
    if (!node.data?.cards) return;

    let updatedCards = [];
    for (const [index, card] of node.data.cards.entries()) {
      const buttonsCard = card.components.find((c) => c.type === "BUTTONS");
      if (!buttonsCard) {
        updatedCards.push(card);
        continue;
      }

      const quickReplies = buttonsCard.buttons.filter(
        (c) => c.type === "QUICK_REPLY" || c.type === "quick_reply",
      );
      const urlButtons = buttonsCard.buttons.filter(
        (c) => c.type === "URL" || c.type === "url",
      );

      await processQuickRepliesInCards(
        quickReplies,
        user,
        config,
        scenarioId,
        api,
        flowNodeRecord,
      );
      await processUrlButtonsInCards(
        urlButtons,
        index,
        user,
        config,
        api,
        officialWhatsappTemplate,
        whatsappTemplateId,
        flowNodeRecord.id,
      );

      updatedCards.push({ ...card });
    }

    node.data.cards = updatedCards;

    await api.whatsappTemplate.update(whatsappTemplateId, {
      cards: updatedCards,
    });
  }

  async function processQuickRepliesInCards(
    quickReplies,
    user,
    config,
    scenarioId,
    api,
    flowNodeRecord,
  ) {
    if (!quickReplies || quickReplies.length === 0) return;

    for (const quickReply of quickReplies) {
      const id = v4();
      const replyButtonNode = await api.flowNode.create({
        flowNode: {
          customer: { _link: user?.customerId },
          type: "reply_button",
          scenario: { _link: scenarioId },
          parentNode: { _link: flowNodeRecord.id },
          replyButton: {
            create: {
              messageSkeleton: {
                _link: flowNodeRecord.whatsappTemplate.messageSkeleton.id,
              },
              customer: { _link: user?.customerId },
              whatsappId: id,
              title: quickReply.text,
            },
          },
        },
      });

      if (replyButtonNode?.id) {
        const record = await api.flowNode.findById(replyButtonNode.id, {
          select: {
            id: true,
            replyButton: { id: true },
          },
        });
        quickReply.replyButtonId = record.replyButton.id;
        quickReply.gadgetId = record.id;
      }
    }
  }

  async function processUrlButtonsInCards(
    urlButtons,
    cardIndex,
    user,
    config,
    api,
    officialWhatsappTemplate,
    whatsappTemplateId,
    flowNodeRecordId,
  ) {
    if (!urlButtons) return;

    let officialUrls = null;

    if (officialWhatsappTemplate) {
      officialUrls =
        officialWhatsappTemplate.urlButtonsInCards?.find(
          (c) => c.cardIndex === cardIndex,
        )?.buttons || [];
    }

    for (let i = 0; i < urlButtons.length; i++) {
      const urlButton = urlButtons[i];

      const id = v4();

      if (
        (urlButton.gadgetId ?? "") === "" &&
        !urlButton?.url?.includes("{{1}}")
      ) {
        const urlButtonNode = await api.flowNode.create(
          {
            flowNode: {
              customer: {
                _link: user?.customerId ?? config.customerId,
              },
              type: "url_button",
              scenario: { _link: scenarioId },
              parentNode: { _link: flowNodeRecordId },
              redirection: {
                create: {
                  customer: { _link: user?.customerId ?? config.customerId },
                  url: officialUrls ? officialUrls[i]?.url : urlButton?.url,
                  uuid: id,
                  whatsappTemplateId: whatsappTemplateId,
                },
              },
            },
          },
          {
            select: {
              id: true,
              redirection: {
                id: true,
                uuid: true,
                whatsappTemplateId: true,
                url: true,
              },
            },
          },
        );

        if (urlButtonNode?.redirection?.id) {
          urlButton.gadgetId = urlButtonNode.id;
          urlButton.redirectionUrlId = urlButtonNode?.redirection.id;
          urlButton.redirectionUrlUuid = urlButtonNode?.redirection.uuid;
          urlButton.url = urlButtonNode?.redirection.url;
        }
      }
    }
  }

  async function processUrlButtons(
    urlButtons,
    node,
    user,
    config,
    api,
    flowNodeRecord,
    shouldBeReplaced,
  ) {
    let hasUrlButtons = false;

    for (let i = 0; i < urlButtons.length; i++) {
      const b = urlButtons[i];

      if (b.url?.includes("{{1}}")) {
        continue;
      }

      if (shouldBeReplaced || !b.gadgetId) {
        const urlButtonNode = await api.flowNode.create({
          flowNode: {
            customer: {
              _link: user?.customerId ?? config.customerId,
            },
            type: "url_button",
            scenario: { _link: scenarioId },
            parentNode: { _link: node.data.nodeId },
          },
        });

        const isFirstButton = !hasUrlButtons;

        // b.gadgetId = urlButtonNode.id;
        if (isFirstButton) {
          node.data.urlButton.gadgetId = urlButtonNode.id;
        } else {
          node.data.urlButton2.gadgetId = urlButtonNode.id;
        }

        let redirectionId;
        if (flowNodeRecord) {
          redirectionId = isFirstButton
            ? flowNodeRecord.whatsappTemplate?.redirectionUrl?.id
            : flowNodeRecord.whatsappTemplate?.redirectionUrl2?.id;
        } else {
          redirectionId = isFirstButton
            ? node.data.urlButton?.redirectionUrlId
            : node.data.urlButton2?.redirectionUrlId;
        }

        if (redirectionId) {
          await api.redirection.update(redirectionId, {
            flowNode: { _link: urlButtonNode.id },
          });

          b.redirectionUrlId = redirectionId;
        }

        if (isFirstButton) {
          hasUrlButtons = true;
        }
      }
    }
  }

  async function nodeLoop() {
    let gaiaWebsiteDatasourcesModified = false;
    for (var k = 0; k < nodes.length; k++) {
      const node = nodes[k];
      let urlButtons = [];

      const { urlButtonLink, urlButtonLink2 } = _formatURLButtons(node.data);

      // Known Node
      if (node.data.nodeId ?? "" != "") {
        var nodeType;
        var nodeData;

        if (node.data?.urlButton) {
          urlButtons.push(node.data?.urlButton);
        }
        if (node.data?.urlButton2) {
          urlButtons.push(node.data?.urlButton2);
        }

        if (node.type === "trigger") {
          nodeType = "whatsappTrigger";
          nodeData = {
            id: node.data.whatsappTriggerId,
            type: node.data.type,
            text: node.data.text?.trim(),
          };
        } else if (node.type === "message") {
          nodeType = "messageSkeleton";
          nodeData = _formatNodeData(
            node,
            { urlButtonLink, urlButtonLink2 },
            { update: true },
          );
        } else if (node.type === "template") {
          console.log("TEMPLATE");
          /*
           * SP-9214 TOREMOVE
           */
          if (node.data.shouldBeConverted) {
            nodeType = "messageSkeleton";
            await convertTemplateInAnswer({
              api,
              node,
              nodeData,
              campaignId,
              urlButtonLink,
              user,
              scenarioId,
              edges,
            });
            continue;
          } else if (node.data.shouldBeReplaced) {
            node.data.buttons = node.data.buttons.map((button) => {
              return { title: button.title };
            });

            console.log(node);
            console.log(edges);
            let type;
            let nodeType;
            let nodeData;
            console.log("delete buttons");
            try {
              if (node.data.oldData?.whatsappTemplateId) {
                let buttons = node.data.oldData?.buttons;
                const cards = node.data.oldData?.cards;
                if (cards) {
                  const buttonsInCards = cards.flatMap((card) =>
                    card.components
                      .filter((component) => component.type === "BUTTONS")
                      .flatMap((component) =>
                        component.buttons.filter(
                          (button) =>
                            button.type === "QUICK_REPLY" ||
                            (button.type === "URL" &&
                              !button.url.includes("{{1}}")),
                        ),
                      ),
                  );

                  buttons = [...buttons, ...buttonsInCards];
                }

                const { urlButton: _urlButton, urlButton2: _urlButton2 } =
                  node.data.oldData;

                if (_urlButton) {
                  buttons = [...buttons, _urlButton];
                }
                if (_urlButton2) {
                  buttons = [...buttons, _urlButton2];
                }

                if (buttons?.length) {
                  console.log("delete buttons in try");
                  const buttonsIds = buttons
                    .map(({ gadgetId }) => gadgetId)
                    .filter((i) => !!i);
                  console.log(buttonsIds);
                  await api.flowNode.bulkDelete(buttonsIds);
                  console.log(
                    `Buttons with gadgetId ${JSON.stringify(buttonsIds || [])} deleted`,
                  );
                }
              }
            } catch (error) {
              console.log("no button to delete", error);
            }
            try {
              if (node.data.nodeId && node.data.shouldBeReplaced) {
                console.log("node to delete", node);
                await api.flowNode.delete(node.data.nodeId);
                const buttonNodesToDelete = await api.flowNode.findMany({
                  filter: {
                    type: { in: ["reply_button", "url_button"] },
                    parentNodeId: { equals: node.data.nodeId },
                  },
                  select: { id: true },
                });
                console.log("button nodes to delete", buttonNodesToDelete);
                if (buttonNodesToDelete.length > 0) {
                  await api.flowNode.bulkDelete(
                    buttonNodesToDelete.map((b) => b.id),
                  );
                }
              }
            } catch (error) {
              console.log("no node to delete", error);
            }

            node.data.shouldBeReplaced = false;
            if (!node.data.isEditing) {
              node.data.oldData = null;
            }

            // Création d'un nouveau template
            const officialWhatsappTemplate =
              await api.officialWhatsappTemplate.maybeFindFirst({
                filter: {
                  name: { equals: node.data.whatsappTemplateName },
                  customerId: { equals: user?.customerId },
                },
              });

            type = "whatsapp_template";
            nodeType = "whatsappTemplate";

            const { limitedTimeOffer } = node.data;
            const {
              expirationDelayInMinutes,
              expirationType,
              expirationDate,
              text: limitedTimeOfferText,
            } = limitedTimeOffer || {};

            const shouldCreateRedirection =
              !officialWhatsappTemplate &&
              node.data?.urlButton &&
              node.data?.urlButton?.trackingEnabled !== false &&
              (!node.data?.urlButton?.url ||
                !node.data.urlButton.url.includes("{{1}}"));

            const shouldCreateRedirection2 =
              !officialWhatsappTemplate &&
              node?.data?.urlButton2 &&
              node.data?.urlButton2?.trackingEnabled !== false &&
              (!node.data?.urlButton2?.url ||
                !node.data.urlButton2.url.includes("{{1}}"));

            nodeData = {
              customer: { _link: user?.customerId },
              whatsappTemplateName: node.data.whatsappTemplateName,
              headerParameters: node.data.headerParameters,
              bodyParameters: node.data.bodyParameters,
              buttonParameters: node.data.buttonParameters,
              isEditing: node.data.isEditing,
              isCancelable: node.data.isCancelable,
              copyCode: node.data.copyCodeButton?.text,
              isCopyCodeStatic: true,
              expirationDelayInMinutes,
              expirationType,
              expirationDate,
              limitedTimeOfferText,
              cards: node.data.cards,
              messageSkeleton: {
                create: {
                  customer: { _link: user?.customerId },
                  body: node.data.body,
                  footer: node.data.footer,
                  imageLink: node.data.headerParameters?.image?.link,
                  videoLink: node.data.headerParameters?.video?.link,
                  audioLink: node.data.headerParameters?.audio?.link,
                  campaign: { _link: campaignId },
                  isCampaignTrigger: node.data.isTemplate,
                  urlButtonText: node.data?.urlButton?.text,
                  urlButtonLink: urlButtonLink,
                  urlButtonText2: node.data?.urlButton2?.text,
                  urlButtonLink2: urlButtonLink2,
                  isUrlButtonAtBottom: node.data?.isUrlButtonAtBottom,
                  shopifyUpsellLineItems: node.data?.shopifyUpsellLineItems,
                },
              },

              ...(officialWhatsappTemplate?.buttonUrlLink && {
                redirectionUrl: {
                  create: {
                    customer: { _link: user?.customerId },
                    url: officialWhatsappTemplate.buttonUrlLink,
                    uuid: v4(),
                  },
                },
              }),
              ...(shouldCreateRedirection && {
                redirectionUrl: {
                  create: {
                    customer: { _link: user?.customerId },
                    url: node.data?.urlButton?.url,
                    uuid: v4(),
                  },
                },
              }),
              ...(officialWhatsappTemplate?.buttonUrlLink2 && {
                redirectionUrl2: {
                  create: {
                    customer: { _link: user?.customerId },
                    url: officialWhatsappTemplate.buttonUrlLink2,
                    uuid: v4(),
                  },
                },
              }),
              ...(shouldCreateRedirection2 && {
                redirectionUrl2: {
                  create: {
                    customer: { _link: user?.customerId },
                    url: node.data?.urlButton?.url,
                    uuid: v4(),
                  },
                },
              }),
            };

            console.log("create");
            console.log(nodeData);
            const flowNodeRecord = await api.flowNode.create(
              {
                flowNode: {
                  type,
                  customer: { _link: user?.customerId },
                  scenario: { _link: scenarioId },
                  positionX: Math.round(node.position.x),
                  positionY: Math.round(node.position.y),
                  [nodeType]: {
                    create: nodeData,
                  },
                },
              },
              {
                select: {
                  id: true,
                  messageSkeleton: { id: true },
                  whatsappTemplate: {
                    id: true,
                    messageSkeleton: { id: true },
                    redirectionUrl: { id: true },
                    redirectionUrl2: { id: true },
                  },
                  aiConfiguration: { id: true },
                  waitingNode: { id: true },
                },
              },
            );

            if (node.data?.cards) {
              const whatsappTemplateId = flowNodeRecord.whatsappTemplate.id;

              await processNodeCards(
                node,
                user,
                config,
                scenarioId,
                api,
                officialWhatsappTemplate,
                whatsappTemplateId,
                flowNodeRecord,
              );
            }
            console.log(flowNodeRecord);
            console.log(campaignId);

            node.data.nodeId = flowNodeRecord.id;
            node.data.whatsappTemplateId = flowNodeRecord.whatsappTemplate.id;
            node.data.messageSkeletonId =
              flowNodeRecord.whatsappTemplate.messageSkeleton.id;

            // If campaign trigger
            if (node.data.isTemplate && flowNodeRecord) {
              console.log("update campaign trigger");
              node.data.isTemplate = true;
              if (flowNodeRecord?.whatsappTemplate?.id) {
                try {
                  await api.campaign.update(campaignId, {
                    whatsappTemplate: {
                      _link: flowNodeRecord.whatsappTemplate.id,
                    },
                  });
                  console.log("Campagne mise à jour avec succès");
                } catch (error) {
                  console.error(
                    "Erreur lors de la mise à jour de la campagne : ",
                    error,
                  );
                }
              } else {
                console.error(
                  "Erreur : whatsappTemplate ID est manquant ou invalide",
                );
              }
            }

            if (node.data.buttons.length > 0) {
              const buttons = node.data.buttons;

              for (let i = 0; i < buttons.length; i++) {
                const b = buttons[i];

                if ((b.gadgetId ?? "") === "") {
                  const id = v4();
                  b.whatsappId = id;
                  const replyButtonNode = await api.flowNode.create({
                    flowNode: {
                      customer: {
                        _link: user?.customerId,
                      },
                      type: "reply_button",
                      scenario: { _link: scenarioId },
                      parentNode: { _link: node.data.nodeId },
                      replyButton: {
                        create: {
                          messageSkeleton: {
                            _link: node.data.messageSkeletonId,
                          },
                          customer: {
                            _link: user?.customerId,
                          },
                          whatsappId: id,
                          title: b.title,
                        },
                      },
                    },
                  });
                  b.gadgetId = replyButtonNode.id;
                  const record = await api.flowNode.findById(
                    replyButtonNode.id,
                    {
                      select: { replyButton: { id: true } },
                    },
                  );
                  b.replyButtonGadgetId = record.replyButton.id;
                } else {
                  api.replyButton.update(b.replyButtonGadgetId, {
                    replyButton: {
                      messageSkeleton: { _link: node.data.messageSkeletonId },
                      title: b.title,
                    },
                  });
                }
              }
            }

            if (urlButtons.length > 0) {
              await processUrlButtons(
                urlButtons,
                node,
                user,
                config,
                api,
                flowNodeRecord,
                node.data.shouldBeReplaced,
              );
            }
          } else {
            if (!urlButtons[0]?.gadgetId || !urlButtons[1]?.gadgetId) {
              if (urlButtons.length > 0) {
                await processUrlButtons(
                  urlButtons,
                  node,
                  user,
                  config,
                  api,
                  null,
                  node.data.shouldBeReplaced,
                );
              }
            }

            if (node.data.cards) {
              const hasMissingGadgetId = node.data.cards.some((card) =>
                card.components?.some(
                  (component) =>
                    component.type === "BUTTONS" &&
                    component.buttons?.some(
                      (button) =>
                        button.type === "URL" &&
                        (button.gadgetId === undefined ||
                          button.gadgetId === null ||
                          button.gadgetId === ""),
                    ),
                ),
              );

              if (hasMissingGadgetId) {
                let updatedCards = [];
                for (const [index, card] of node.data.cards.entries()) {
                  const buttonsCard = card.components.find(
                    (c) => c.type === "BUTTONS",
                  );
                  if (!buttonsCard) {
                    updatedCards.push(card);
                    continue;
                  }
                  const urlButtons = buttonsCard.buttons.filter(
                    (c) => c.type === "URL" || c.type === "url",
                  );
                  const officialWhatsappTemplate =
                    await api.officialWhatsappTemplate.maybeFindFirst({
                      filter: {
                        name: { equals: node.data.whatsappTemplateName },
                        customerId: {
                          equals: user?.customerId ?? config.customerId,
                        },
                      },
                    });
                  await processUrlButtonsInCards(
                    urlButtons,
                    index,
                    user,
                    config,
                    api,
                    officialWhatsappTemplate,
                    node.data.whatsappTemplateId,
                    node.data.nodeId,
                  );

                  updatedCards.push({ ...card });
                }

                node.data.cards = updatedCards;

                if (node.data.whatsappTemplateId) {
                  await api.whatsappTemplate.update(
                    node.data.whatsappTemplateId,
                    {
                      cards: updatedCards,
                    },
                  );
                }
              }
            }
            if (!node.data.isEditing) {
              node.data.oldData = null;
            }
            if (node.data.whatsappTemplateId) {
              const { limitedTimeOffer } = node.data;
              const {
                expirationDelayInMinutes,
                expirationType,
                expirationDate,
              } = limitedTimeOffer || {};
              api.whatsappTemplate.update(node.data.whatsappTemplateId, {
                headerParameters: node.data?.headerParameters,
                bodyParameters: node.data?.bodyParameters,
                copyCode: node.data?.copyCodeButton?.text || null,
                isEditing: node.data?.isEditing,
                expirationDelayInMinutes,
                expirationType,
                expirationDate,
                cards: node.data.cards,
              });
            }
            // Update message skeleton for template nodes to save shopifyUpsellLineItems
            if (node.data.messageSkeletonId) {
              api.messageSkeleton.update(node.data.messageSkeletonId, {
                messageSkeleton: {
                  shopifyUpsellLineItems: node.data?.shopifyUpsellLineItems,
                },
              });
            }
            if (node.data.nodeId) {
              api.flowNode.update(node.data.nodeId, {
                flowNode: {
                  positionX: Math.round(node.position.x),
                  positionY: Math.round(node.position.y),
                },
              });
            }
          }
          continue;
        } else if (node.type === "consent") {
          api.flowNode.update(node.data.nodeId, {
            flowNode: {
              positionX: Math.round(node.position.x),
              positionY: Math.round(node.position.y),
              isOptIn: node.data.type === "optin",
              isOptOut: node.data.type === "optout",
            },
          });
          continue;
        } else if (node.type === "wait") {
          nodeType = "waitingNode";
          nodeData = {
            id: node.data.waitingNodeId,
            type: node.data.type === "" ? null : node.data.type,
            durationInMinutes: parseInt(node.data.duration),
            messageStatus: "read",
            storeAnswer:
              node.data.type === "whatsapp_message"
                ? (node.data.storeAnswer ?? false)
                : null,
            storageField:
              node.data.type === "whatsapp_message"
                ? (node.data.storageField ?? null)
                : null,
          };
        } else if (node.type === "note") {
          api.flowNode.update(node.data.nodeId, {
            flowNode: {
              positionX: Math.round(node.position.x),
              positionY: Math.round(node.position.y),
              note: node.data.note,
            },
          });
          continue;
        } else if (node.type === "tag") {
          api.flowNode.update(node.data.nodeId, {
            flowNode: {
              positionX: Math.round(node.position.x),
              positionY: Math.round(node.position.y),
              isTag: true,
              tag: { _link: node.data.tagId },
            },
          });
          continue;
        } else if (node.type === "condition") {
          nodeType = "condition";
          nodeData = {
            id: node.data.conditionId,
            operator: node.data.operator ?? null,
            property: node.data.property ?? null,
            valueBoolean: node.data.valueBoolean ?? null,
            valueStartingPeriodInMinutes:
              parseInt(node.data.valueStartingPeriodInMinutes, 10) ?? null,
            valueType: node.data.valueType ?? null,
            valueTag: node.data.valueTag.id
              ? { _link: node.data.valueTag.id }
              : null,
            valuePercentage: node.data.valuePercentage ?? null,
            conditionFalseCount: node.data.falseCount,
            conditionTrueCount: node.data.trueCount,
          };
        } else if (node.type === "webhook") {
          nodeType = "webhookNode";
          nodeData = {
            id: node.data.webhookId,
            name: node.data.name ?? null,
            method: node.data.method === "" ? null : node.data.method,
            destinationUrl:
              node.data.destinationUrl.trim() === ""
                ? null
                : node.data.destinationUrl.trim().startsWith("http")
                  ? node.data.destinationUrl
                      .trim()
                      .replace(/^http:\/\//, "https://")
                  : `https://${node.data.destinationUrl.trim()}`,
            headers:
              node.data.headers.length === 1 && node.data.headers[0].key === ""
                ? null
                : node.data.headers,
            queryParams:
              node.data.queryParams.length === 1 &&
              node.data.queryParams[0].key === ""
                ? null
                : node.data.queryParams,
            pathParams:
              node.data.pathParams.length === 1 &&
              node.data.pathParams[0].key === ""
                ? null
                : node.data.pathParams,
            configResponse:
              node.data.configResponse.length === 1 &&
              node.data.configResponse[0].key === ""
                ? null
                : node.data.configResponse,
            body:
              node.data.body === "" ? null : forceJsonFormat(node.data.body),
          };
        } else if (node.type === "ai") {
          const _gaiaWebsiteDatasources = (
            node.data?.gaiaWebsiteDatasources || []
          ).filter(({ url }) => url !== "");
          if (
            !gaiaWebsiteDatasourcesModified &&
            _gaiaWebsiteDatasources?.length
          ) {
            const gaiaWebsiteDatasources =
              await api.generateGaiaWebsiteDatasource({
                gaiaWebsiteDatasources: _gaiaWebsiteDatasources,
                title: `${user.customer.name} - chatbot`,
              });
            await api.customer.update(user?.customerId, {
              gaiaWebsiteDatasources,
            });
            gaiaWebsiteDatasourcesModified = true;
          }

          nodeType = "aiConfiguration";
          nodeData = {
            id: node.data.aiConfigurationId,
            answerStyle: node.data.answerStyle,
            assistantContext: node.data.assistantContext,
            companyContext: node.data.companyContext,
          };
        }

        if (node.type !== "automationTrigger") {
          api.flowNode.update(node.data.nodeId, {
            flowNode: {
              positionX: Math.round(node.position.x),
              positionY: Math.round(node.position.y),
              [nodeType]: {
                update: nodeData,
              },
            },
          });
        }
      } else {
        let type;
        let nodeType;
        let nodeData;

        if (node.type === "trigger") {
          type = "whatsapp_trigger";
          nodeType = "whatsappTrigger";
          nodeData = {
            id: node.data.whatsappTriggerId,
            type: node.data.type,
            text: node.data.text.trim(),
          };
        } else if (node.type === "message") {
          type = "message_skeleton";
          nodeType = "messageSkeleton";
          nodeData = _formatNodeData(node, { urlButtonLink, urlButtonLink2 });
        } else if (node.type === "template") {
          const officialWhatsappTemplate =
            await api.officialWhatsappTemplate.maybeFindFirst({
              filter: {
                name: { equals: node.data.whatsappTemplateName },
                customerId: { equals: user?.customerId },
              },
            });

          type = "whatsapp_template";
          nodeType = "whatsappTemplate";
          const { limitedTimeOffer } = node.data;
          const {
            expirationDelayInMinutes,
            expirationType,
            expirationDate,
            text: limitedTimeOfferText,
          } = limitedTimeOffer || {};

          const shouldCreateRedirection =
            !officialWhatsappTemplate &&
            node.data?.urlButton &&
            node.data?.urlButton?.trackingEnabled !== false &&
            (!node.data?.urlButton?.url ||
              !node.data.urlButton.url.includes("{{1}}"));

          const shouldCreateRedirection2 =
            !officialWhatsappTemplate &&
            node?.data?.urlButton2 &&
            node.data?.urlButton2?.trackingEnabled !== false &&
            (!node.data?.urlButton2?.url ||
              !node.data.urlButton2.url.includes("{{1}}"));

          nodeData = {
            customer: { _link: user?.customerId },
            whatsappTemplateName: node.data.whatsappTemplateName,
            headerParameters: node.data.headerParameters,
            bodyParameters: node.data.bodyParameters,
            buttonParameters: node.data.buttonParameters,
            isEditing: node.data.isEditing,
            copyCode: node.data.copyCodeButton?.text,
            isCopyCodeStatic: true,
            expirationDelayInMinutes,
            expirationType,
            expirationDate,
            limitedTimeOfferText,
            cards: node.data.cards,
            messageSkeleton: {
              create: {
                customer: { _link: user?.customerId },
                body: node.data.body,
                footer: node.data.footer,
                imageLink: node.data.headerParameters?.image?.link,
                videoLink: node.data.headerParameters?.video?.link,
                audioLink: node.data.headerParameters?.audio?.link,
                campaign: { _link: campaignId },
                isCampaignTrigger: false,
                urlButtonText: node.data?.urlButton?.text,
                urlButtonLink: urlButtonLink,
                urlButtonText2: node.data?.urlButton2?.text,
                urlButtonLink2: urlButtonLink2,
                isUrlButtonAtBottom: node.data?.isUrlButtonAtBottom,
                shopifyUpsellLineItems: node.data?.shopifyUpsellLineItems,
              },
            },
            ...(officialWhatsappTemplate?.buttonUrlLink && {
              redirectionUrl: {
                create: {
                  customer: { _link: user?.customerId },
                  url: officialWhatsappTemplate.buttonUrlLink,
                  uuid: v4(),
                },
              },
            }),
            ...(shouldCreateRedirection && {
              redirectionUrl: {
                create: {
                  customer: { _link: user?.customerId },
                  url: node.data?.urlButton?.url,
                  uuid: v4(),
                },
              },
            }),
            ...(officialWhatsappTemplate?.buttonUrlLink2 && {
              redirectionUrl2: {
                create: {
                  customer: { _link: user?.customerId },
                  url: officialWhatsappTemplate.buttonUrlLink2,
                  uuid: v4(),
                },
              },
            }),
            ...(shouldCreateRedirection2 && {
              redirectionUrl2: {
                create: {
                  customer: { _link: user?.customerId },
                  url: node.data?.urlButton2?.url,
                  uuid: v4(),
                },
              },
            }),
          };
        } else if (node.type === "consent") {
          const flowNodeRecord = await api.flowNode.create({
            flowNode: {
              type: "consent",
              customer: { _link: user?.customerId },
              scenario: { _link: scenarioId },
              positionX: Math.round(node.position.x),
              positionY: Math.round(node.position.y),
              isOptIn: node.data.type === "optin",
              isOptOut: node.data.type === "optout",
            },
          });
          node.data.nodeId = flowNodeRecord.id;
          continue;
        } else if (node.type === "wait") {
          type = "waiting_node";
          nodeType = "waitingNode";
          nodeData = {
            customer: { _link: user?.customerId },
            type: node.data.type === "" ? null : node.data.type,
            durationInMinutes: parseInt(node.data.duration),
            messageStatus: "read",
            storeAnswer:
              node.data.type === "whatsapp_message"
                ? (node.data.storeAnswer ?? false)
                : null,
            storageField:
              node.data.type === "whatsapp_message"
                ? (node.data.storageField ?? null)
                : null,
          };
        } else if (node.type === "note") {
          const flowNodeRecord = await api.flowNode.create({
            flowNode: {
              type: "note",
              customer: { _link: user?.customerId },
              scenario: { _link: scenarioId },
              positionX: Math.round(node.position.x),
              positionY: Math.round(node.position.y),
              note: node.data.note,
            },
          });
          node.data.nodeId = flowNodeRecord.id;
          continue;
        } else if (node.type === "condition") {
          type = "condition";
          nodeType = "condition";
          nodeData = {
            customer: { _link: user?.customerId },
            operator: node.data.operator ?? null,
            property: node.data.property ?? null,
            valueBoolean: node.data.valueBoolean ?? null,
            valueStartingPeriodInMinutes:
              parseInt(node.data.valueStartingPeriodInMinutes, 10) ?? null,
            valueType: node.data.valueType ?? null,
            valueTag: node.data.valueTag?.id
              ? { _link: node.data.valueTag?.id }
              : null,
            valuePercentage: node.data.valuePercentage ?? null,
          };
        } else if (node.type === "webhook") {
          type = "webhook_node";
          nodeType = "webhookNode";
          nodeData = {
            customer: { _link: user?.customerId },
            name: node.data.name ?? null,
            method: node.data.method === "" ? null : node.data.method,
            destinationUrl:
              node.data.destinationUrl.trim() === ""
                ? null
                : node.data.destinationUrl.trim().startsWith("https://")
                  ? node.data.destinationUrl.trim()
                  : `https://${node.data.destinationUrl.trim()}`,
            headers: node.data.headers === "" ? null : node.data.headers,
            queryParams:
              node.data.queryParams === "" ? null : node.data.queryParams,
            pathParams:
              node.data.pathParams === "" ? null : node.data.pathParams,
            configResponse:
              node.data.configResponse === "" ? null : node.data.configResponse,
            body:
              node.data.body === "" ? null : forceJsonFormat(node.data.body),
          };
        } else if (node.type === "tag") {
          const flowNodeRecord = await api.flowNode.create({
            flowNode: {
              type: "tag",
              customer: { _link: user?.customerId },
              scenario: { _link: scenarioId },
              positionX: Math.round(node.position.x),
              positionY: Math.round(node.position.y),
              isTag: true,
              tag: { _link: node.data.tagId },
            },
          });
          node.data.nodeId = flowNodeRecord.id;
          continue;
        } else if (node.type === "ai") {
          type = "ai";
          nodeType = "aiConfiguration";
          nodeData = {
            customer: { _link: user?.customerId },
            answerStyle: node.data.answerStyle,
            assistantContext: node.data.assistantContext,
            companyContext: node.data.companyContext,
          };
        }

        const flowNodeRecord = await api.flowNode.create(
          {
            flowNode: {
              type,
              customer: { _link: user?.customerId },
              scenario: { _link: scenarioId },
              positionX: Math.round(node.position.x),
              positionY: Math.round(node.position.y),
              [nodeType]: {
                create: nodeData,
              },
            },
          },
          {
            select: {
              id: true,
              messageSkeleton: { id: true },
              whatsappTemplate: {
                id: true,
                messageSkeleton: { id: true },
                redirectionUrl: { id: true },
                redirectionUrl2: { id: true },
              },
              aiConfiguration: { id: true },
              waitingNode: { id: true },
              condition: { id: true },
              webhookNode: { id: true },
            },
          },
        );
        node.data.nodeId = flowNodeRecord.id;

        if (node.data?.urlButton) {
          urlButtons.push(node.data?.urlButton);
        }
        if (node.data?.urlButton2) {
          urlButtons.push(node.data?.urlButton2);
        }

        //changer cette condition pour ajouter un flowNode sur les url_Buttons dans les messages
        if (urlButtons.length > 0 && node.type === "template") {
          processUrlButtons(
            urlButtons,
            node,
            user,
            config,
            api,
            flowNodeRecord,
            node.data.shouldBeReplaced,
          );
        }

        if (node.type === "message") {
          node.data.messageSkeletonId = flowNodeRecord.messageSkeleton.id;
          // Update redirectionUrlId if redirection was created
          if (
            flowNodeRecord.messageSkeleton?.redirectionUrl?.id &&
            node.data?.urlButton
          ) {
            node.data.urlButton.redirectionUrlId =
              flowNodeRecord.messageSkeleton.redirectionUrl.id;
          }
        } else if (node.type === "template") {
          node.data.messageSkeletonId =
            flowNodeRecord.whatsappTemplate.messageSkeleton.id;
          // Update redirectionUrlId if redirection was created
          if (
            flowNodeRecord.whatsappTemplate?.redirectionUrl?.id &&
            node.data?.urlButton
          ) {
            node.data.urlButton.redirectionUrlId =
              flowNodeRecord.whatsappTemplate.redirectionUrl.id;
          }
          if (
            flowNodeRecord.whatsappTemplate?.redirectionUrl2?.id &&
            node.data?.urlButton2
          ) {
            node.data.urlButton2.redirectionUrlId =
              flowNodeRecord.whatsappTemplate.redirectionUrl2.id;
          }

          if (node.data?.cards) {
            const whatsappTemplateId = flowNodeRecord.whatsappTemplate.id;
            node.data.whatsappTemplateId = flowNodeRecord.whatsappTemplate.id;
            const officialWhatsappTemplate =
              await api.officialWhatsappTemplate.maybeFindFirst({
                filter: {
                  name: { equals: node.data.whatsappTemplateName },
                  customerId: { equals: user?.customerId },
                },
              });
            await processNodeCards(
              node,
              user,
              config,
              scenarioId,
              api,
              officialWhatsappTemplate,
              whatsappTemplateId,
              flowNodeRecord,
            );
          }
        } else if (node.type === "wait") {
          node.data.waitingNodeId = flowNodeRecord.waitingNode.id;
        } else if (node.type === "ai") {
          node.data.aiConfigurationId = flowNodeRecord.aiConfiguration.id;
        } else if (node.type === "condition") {
          node.data.conditionId = flowNodeRecord.condition.id;
        } else if (node.type === "webhook") {
          node.data.webhookId = flowNodeRecord.webhookNode.id;
        }
      }

      if (node.type === "message" || node.type === "template") {
        const buttons = node.data?.buttons || [];

        // Interactive reply list
        if (node.data?.type === "list") {
          for (let i = 0; i < buttons.length; i++) {
            const b = buttons[i];

            if ((b.gadgetId ?? "") === "") {
              const id = v4();
              b.whatsappId = id;
              const replyListNode = await api.flowNode.create({
                flowNode: {
                  customer: { _link: user?.customerId },
                  type: "reply_list",
                  scenario: { _link: scenarioId },
                  parentNode: { _link: node.data.nodeId },
                  replyList: {
                    create: {
                      messageSkeleton: { _link: node.data.messageSkeletonId },
                      customer: {
                        _link: user?.customerId,
                      },
                      whatsappId: id,
                      title: b.title,
                      description: b?.description,
                    },
                  },
                },
              });
              b.gadgetId = replyListNode.id;
              const record = await api.flowNode.findById(replyListNode.id, {
                select: { replyList: { id: true } },
              });
              b.replyListGadgetId = record.replyList.id;
            } else {
              api.replyList.update(b.replyListGadgetId, {
                replyList: {
                  messageSkeleton: { _link: node.data.messageSkeletonId },
                  title: b.title,
                  description: b?.description,
                },
              });
            }
          }
        }

        // Interactive reply buttons
        else {
          for (let i = 0; i < buttons.length; i++) {
            const b = buttons[i];

            if ((b.gadgetId ?? "") === "") {
              const id = v4();
              b.whatsappId = id;
              const replyButtonNode = await api.flowNode.create({
                flowNode: {
                  customer: { _link: user?.customerId },
                  type: "reply_button",
                  scenario: { _link: scenarioId },
                  parentNode: { _link: node.data.nodeId },
                  replyButton: {
                    create: {
                      messageSkeleton: { _link: node.data.messageSkeletonId },
                      customer: {
                        _link: user?.customerId,
                      },
                      whatsappId: id,
                      title: b.title,
                    },
                  },
                },
              });
              b.gadgetId = replyButtonNode.id;
              const record = await api.flowNode.findById(replyButtonNode.id, {
                select: { replyButton: { id: true } },
              });
              b.replyButtonGadgetId = record.replyButton.id;
            } else {
              api.replyButton.update(b.replyButtonGadgetId, {
                replyButton: {
                  messageSkeleton: { _link: node.data.messageSkeletonId },
                  title: b.title,
                },
              });
            }
          }
        }
      }
    }
  }

  const invalidSourceHandleForQuickReply = new Set([
    null,
    undefined,
    "",
    "main",
    "true",
    "false",
  ]);

  async function edgeLoop() {
    console.log("edges before loop", JSON.stringify(edges));
    const handleIndexMapping = [
      "a",
      "b",
      "c",
      "d",
      "e",
      "f",
      "g",
      "h",
      "i",
      "j",
      "k",
      "l",
      "m",
      "n",
      "o",
      "p",
      "q",
      "r",
      "s",
      "t",
    ];
    for (var k = 0; k < edges.length; k++) {
      const edge = edges[k];
      const sourceNode = nodes.find((n) => n.id === edge.source);
      const targetNode = nodes.find((n) => n.id === edge.target);
      let condition = null;

      var sourceId = sourceNode.data.nodeId;
      var targetId = targetNode.data.nodeId;

      // Reply buttons
      if (!invalidSourceHandleForQuickReply.has(edge.sourceHandle)) {
        if (sourceNode.data.cards && Array.isArray(sourceNode.data.cards)) {
          let foundGadgetId = null;
          for (let i = 0; i < sourceNode.data.cards.length; i++) {
            const card = sourceNode.data.cards[i];
            const buttonsComponent = card.components.find(
              (c) => c.type === "BUTTONS",
            );
            if (buttonsComponent && Array.isArray(buttonsComponent.buttons)) {
              const globalIndex = handleIndexMapping.indexOf(edge.sourceHandle);
              const cardIdx = Math.floor(globalIndex / 2);
              if (i === cardIdx) {
                const btnIdx = globalIndex % 2;
                if (buttonsComponent.buttons[btnIdx]?.gadgetId) {
                  foundGadgetId = buttonsComponent.buttons[btnIdx].gadgetId;
                }
              }
            }
          }
          if (foundGadgetId) {
            sourceId = foundGadgetId;
          }
        } else if (
          sourceNode.data.buttons &&
          Array.isArray(sourceNode.data.buttons) &&
          edge.sourceHandle !== "urlA" &&
          edge.sourceHandle !== "urlB"
        ) {
          const buttonIndex = handleIndexMapping.indexOf(edge.sourceHandle);
          if (sourceNode.data.buttons[buttonIndex]?.gadgetId) {
            sourceId = sourceNode.data.buttons[buttonIndex]?.gadgetId;
          }
        } else if (
          edge.sourceHandle === "urlA" &&
          sourceNode?.data?.urlButton?.gadgetId
        ) {
          sourceId = sourceNode.data.urlButton.gadgetId;
        } else if (
          edge.sourceHandle === "urlB" &&
          sourceNode?.data?.urlButton2?.gadgetId
        ) {
          sourceId = sourceNode.data.urlButton2.gadgetId;
        } else if (sourceNode.data.buttons) {
          const buttonIndex = handleIndexMapping.indexOf(edge.sourceHandle);
          if (sourceNode.data.buttons[buttonIndex]?.gadgetId) {
            sourceId = sourceNode.data.buttons[buttonIndex].gadgetId;
          }
        }
      } else if (
        edge.sourceHandle === "true" ||
        edge.sourceHandle === "false"
      ) {
        condition = edge.sourceHandle === "true" ? true : false;
      }

      console.log("saving edges");

      if ((edge?.data?.edgeId ?? "") != "") {
        try {
          await api.flowEdge.update(edge.data.edgeId, {
            flowEdge: {
              sourceNode: { _link: sourceId },
              targetNode: { _link: targetId },
              isSourceTrueCondition: condition,
              isSourceFalseCondition: condition === null ? null : !condition,
            },
          });
        } catch (e) {
          console.log("erreur save edge", e);
          const existingEdge = await api.flowEdge.maybeFindFirst({
            filter: {
              scenarioId: { equals: scenarioId },
              sourceNodeId: { equals: sourceId },
              targetNodeId: { equals: targetId },
            },
          });
          if (!existingEdge) {
            const flowEdgeRecord = await api.flowEdge.create({
              flowEdge: {
                scenario: { _link: scenarioId },
                sourceNode: { _link: sourceId },
                targetNode: { _link: targetId },
                isSourceTrueCondition: condition,
                isSourceFalseCondition: condition === null ? null : !condition,
              },
            });
            edge.data = { edgeId: flowEdgeRecord.id };
          } else {
            console.log("edge already exists", existingEdge);
          }
        }
      }

      // New edge
      else {
        const existingEdge = await api.flowEdge.maybeFindFirst({
          filter: {
            scenarioId: { equals: scenarioId },
            sourceNodeId: { equals: sourceId },
            targetNodeId: { equals: targetId },
          },
        });
        if (!existingEdge) {
          const flowEdgeRecord = await api.flowEdge.create({
            flowEdge: {
              scenario: { _link: scenarioId },
              sourceNode: { _link: sourceId },
              targetNode: { _link: targetId },
              isSourceTrueCondition: condition,
              isSourceFalseCondition: condition === null ? null : !condition,
            },
          });
          edge.data = { edgeId: flowEdgeRecord.id };
        } else {
          console.log("edge already exists", existingEdge);
        }
      }
    }
  }

  const _formatURLButtons = (nodeData) => {
    if (nodeData) {
      const { urlButton, urlButton2 } = nodeData;
      let urlButtonLink;
      if (urlButton?.url) {
        urlButtonLink = urlButton.url.trim().startsWith("http")
          ? urlButton.url.trim()
          : `http://${urlButton.url.trim()}`;
        urlButtonLink = urlButtonLink.replace(/\{\{[^}]+\}\}/g, "{{1}}");
      }

      let urlButtonLink2;
      if (urlButton2?.url) {
        urlButtonLink2 = urlButton2.url.trim().startsWith("http")
          ? urlButton2.url.trim()
          : `http://${urlButton2.url.trim()}`;
        urlButtonLink2 = urlButtonLink2.replace(/\{\{[^}]+\}\}/g, "{{1}}");
      }

      return { urlButtonLink, urlButtonLink2 };
    }
    return { urlButtonLink: null, urlButtonLink2: null };
  };

  const _formatNodeData = (nodeItem, common, options) => {
    const { type: nodeType, data } = nodeItem;
    switch (nodeType) {
      case "message": {
        return _formatMessageNodeData(data, common, options);
      }
      default:
        return null;
    }
  };

  const _formatMessageNodeData = (data, common, options) => {
    const { urlButtonLink } = common ?? {};
    const {
      type,
      messageSkeletonId,
      image,
      video,
      audio,
      sticker,
      document,
      documentName,
      body,
      bodyParameters,
      header,
      footer,
      buttonTitle,
      urlButton,
      shopifyUpsellLineItems,
    } = data;

    const nodeTypeMedia = type === "media";
    const nodeTypeList = type === "list";

    const nodeData = {
      imageLink: (nodeTypeMedia && image) || null,
      videoLink: (nodeTypeMedia && video) || null,
      audioLink: (nodeTypeMedia && audio) || null,
      documentLink: (nodeTypeMedia && document) || null,
      documentName: (nodeTypeMedia && documentName) || null,
      body: ((nodeTypeMedia || nodeTypeList) && body) || null,
      bodyParameters: bodyParameters || null,
      header: (nodeTypeList && header) || null,
      footer: footer || null,
      buttonTitle: (nodeTypeList && buttonTitle) || null,
      urlButtonText: (nodeTypeMedia && urlButton?.text) || null,
      urlButtonLink: (nodeTypeMedia && urlButtonLink) || null,
      shopifyUpsellLineItems: shopifyUpsellLineItems ?? null,
      redirectionUrl:
        nodeTypeMedia && urlButtonLink && urlButton?.trackingEnabled !== false
          ? {
              create: {
                customer: { _link: user?.customerId },
                url: urlButtonLink,
                uuid: v4(),
              },
            }
          : null,
    };

    if (options?.update) {
      nodeData.id = messageSkeletonId;
      nodeData.stickerLink = sticker ?? null;
    }
    return nodeData;
  };

  await nodeLoop();
  await edgeLoop();

  await api.scenario.update(scenarioId, {
    savedAt: new Date(),
    name: campaignName,
  });

  if (campaignId) {
    try {
      await api.campaign.update(campaignId, {
        name: campaignName,
      });
    } catch (e) {
      console.log(e);
    }
  }
}
